import PropTypes from 'prop-types';
import { useCallback, useState } from 'react';
import useLocalize from '../../utils/hooks/useLocalize';

const CHOICE_RESUME = 'resume';
const CHOICE_START_FRESH = 'startFresh';

/**
 * WelcomeBackScreen
 *
 * STORY (#725): shown in place of the coach mark/blank chat when the AI
 * Scanner opens with a saved session (#724). Offers the member a choice
 * between resuming their previous history and starting a fresh search.
 * Matches Figma `5271:11571`: greeting, two choice buttons (selected one
 * turns green), no bottom swipe panel. The existing overlay's ✕ close
 * button already covers this screen too (same precedent `AiScannerCoachMark`
 * follows) — this component renders no close control of its own.
 *
 * Once a choice is tapped it is applied (green highlight) and the matching
 * callback fires exactly once; a second tap on either button after that is
 * ignored, since the parent is already navigating away.
 *
 * @param {object} props
 * @param {() => void} props.onStartFresh - Called once, after the highlight
 *   is applied, when 「新しく始める」 is tapped.
 * @param {() => void} props.onResume - Called once, after the highlight is
 *   applied, when 「前回の履歴を見る」 is tapped.
 * @returns {JSX.Element}
 */
export const WelcomeBackScreen = ({ onStartFresh, onResume }) => {
  const localize = useLocalize();
  const [selectedChoice, setSelectedChoice] = useState(null);

  const handleChoice = useCallback(
    (choice, callback) => {
      // Edge Case: a rapid double-tap must not fire the callback twice —
      // once a choice is made, further taps on either button are no-ops.
      if (selectedChoice !== null) return;
      setSelectedChoice(choice);
      callback();
    },
    [selectedChoice]
  );

  const handleResume = useCallback(
    () => handleChoice(CHOICE_RESUME, onResume),
    [handleChoice, onResume]
  );
  const handleStartFresh = useCallback(
    () => handleChoice(CHOICE_START_FRESH, onStartFresh),
    [handleChoice, onStartFresh]
  );

  return (
    <div className="ai-scanner-welcome-back">
      <p className="ai-scanner-welcome-back-greeting">
        {localize('ai.scanner.welcomeBack.greeting')}
      </p>
      <div className="ai-scanner-welcome-back-actions">
        <button
          type="button"
          className={`ai-scanner-welcome-back-button${
            selectedChoice === CHOICE_RESUME
              ? ' ai-scanner-welcome-back-button--selected'
              : ''
          }`}
          onClick={handleResume}
        >
          {localize('ai.scanner.welcomeBack.resume')}
        </button>
        <button
          type="button"
          className={`ai-scanner-welcome-back-button${
            selectedChoice === CHOICE_START_FRESH
              ? ' ai-scanner-welcome-back-button--selected'
              : ''
          }`}
          onClick={handleStartFresh}
        >
          {localize('ai.scanner.welcomeBack.startFresh')}
        </button>
      </div>
    </div>
  );
};

WelcomeBackScreen.propTypes = {
  onStartFresh: PropTypes.func.isRequired,
  onResume: PropTypes.func.isRequired
};

export default WelcomeBackScreen;
